import { createSlice } from '@reduxjs/toolkit';
import type { PersonalLearningState, Topic } from './types';

const initialState: PersonalLearningState = {
  pathways: [],
  pathwaysLoading: true,
  pathwaysError: false,
  upNextTopics: [],
  upNextLoading: true,
  upNextError: false,
  topicsPagination: {
    total: 0,
    page: 1,
    pageSize: 10,
    draftTopics: []
  },
  circleGoalsOverview: [],
  circleGoalsLoading: true,
  circleGoalsError: false,
  explorePathways: [],
  explorePathwaysLoading: true,
  recentCompletedTopics: [],
  recentCompletedTopicsLoading: true,
  recentCompletedTopicsError: false,
  recentCompletedTopicsRequestId: null as string | null
};

export const slice = createSlice({
  name: 'personalLearning',
  initialState,
  reducers: {
    startPathwaysLoad(state) {
      state.pathwaysLoading = true;
      state.pathwaysError = false;
    },
    getMyPathways(state, action) {
      state.pathways = action.payload.items;
      state.pathwaysLoading = false;
      state.pathwaysError = false;
    },
    setPathwaysError(state) {
      state.pathwaysError = true;
      state.pathwaysLoading = false;
    },
    startUpNextLoad(state) {
      state.upNextLoading = true;
      state.upNextError = false;
    },
    fetchUpNextTopicsSuccess(state, action) {
      state.upNextTopics = action.payload.filter((topic: Topic) => !topic.topicCompleted);
      state.upNextLoading = false;
      state.upNextError = false;
    },
    setUpNextError(state) {
      state.upNextError = true;
      state.upNextLoading = false;
    },
    startCircleGoalsLoad(state) {
      state.circleGoalsLoading = true;
      state.circleGoalsError = false;
    },
    fetchCircleGoalsOverviewSuccess(state, action) {
      state.circleGoalsOverview = action.payload;
      state.circleGoalsLoading = false;
      state.circleGoalsError = false;
    },
    setCircleGoalsError(state) {
      state.circleGoalsError = true;
      state.circleGoalsLoading = false;
    },
    fetchDraftTopicsSuccess(state, action) {
      state.topicsPagination = {
        ...action.payload.pagination,
        draftTopics: [...state.topicsPagination.draftTopics, ...action.payload.data].filter(
          (topic: Topic) =>
            !topic.topicPublished &&
            !state.upNextTopics.some((ft) => ft.topicGuid === topic.topicGuid)
        )
      };
    },
    clearDraftTopics(state) {
      state.topicsPagination.draftTopics = [];
    },
    removeUpNextTopic(state, action) {
      state.upNextTopics = state.upNextTopics.filter(
        (topic) => topic.topicGuid !== action.payload.topicGuid
      );
      if (!action.payload.topicCompleted) {
        state.topicsPagination.draftTopics = [
          action.payload,
          ...state.topicsPagination.draftTopics
        ];
      }
    },
    addUpNextTopic(state, action) {
      state.upNextTopics.push(action.payload);
      state.topicsPagination.draftTopics = state.topicsPagination.draftTopics.filter(
        (topic) => topic.topicGuid !== action.payload.topicGuid
      );
    },
    startExploreLoad(state) {
      state.explorePathwaysLoading = true;
    },
    fetchExplorePathwaysSuccess(state, action) {
      state.explorePathways = action.payload;
      state.explorePathwaysLoading = false;
    },
    fetchExplorePathwaysFailed(state) {
      state.explorePathways = [];
      state.explorePathwaysLoading = false;
    },
    startRecentCompletedTopicsLoad(state, action) {
      state.recentCompletedTopicsLoading = true;
      state.recentCompletedTopicsError = false;
      state.recentCompletedTopicsRequestId = action.payload;
    },
    fetchRecentCompletedTopicsSuccess(state, action) {
      if (state.recentCompletedTopicsRequestId !== action.payload.requestId) return;
      state.recentCompletedTopics = action.payload.topics;
      state.recentCompletedTopicsLoading = false;
      state.recentCompletedTopicsError = false;
      state.recentCompletedTopicsRequestId = null;
    },
    fetchRecentCompletedTopicsFailed(state, action) {
      if (state.recentCompletedTopicsRequestId !== action.payload) return;
      state.recentCompletedTopicsLoading = false;
      state.recentCompletedTopicsError = true;
      state.recentCompletedTopicsRequestId = null;
    }
  }
});

export default slice.reducer;
